import moment from "moment-timezone";
import { ClassWhen } from "client/openapi";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAYS_LONG = [
  "Mondays",
  "Tuesdays",
  "Wednesdays",
  "Thursdays",
  "Fridays",
  "Saturdays",
  "Sundays",
];

/** "16:00" as "4:00pm". */
export function clockLabel(hhmm: string): string {
  return moment(hhmm, "HH:mm").format("h:mma");
}

/** "4:00 to 5:30pm" for a start and a length. */
function spanLabel(hhmm: string, minutes: number): string {
  const start = moment(hhmm, "HH:mm");
  const end = start.clone().add(minutes, "minutes");
  const sameHalf = start.format("a") === end.format("a");
  return `${start.format(sameHalf ? "h:mm" : "h:mma")} to ${end.format(
    "h:mma"
  )}`;
}

/**
 * The weekly times in one short line: "Mon & Wed 4:00pm, 60 min" when they
 * share a time and length, else each one: "Mon 4:00pm, Wed 5:00pm".
 */
export function weeklyLabel(times: ClassWhen[]): string {
  if (!times.length) return "";
  const same = times.every(
    (t) =>
      t.start_local === times[0].start_local && t.duration === times[0].duration
  );
  if (same)
    return `${times.map((t) => DAYS[t.weekday]).join(" & ")} ${clockLabel(
      times[0].start_local
    )}, ${times[0].duration} min`;
  return times
    .map((t) => `${DAYS[t.weekday]} ${clockLabel(t.start_local)}`)
    .join(", ");
}

/** For families: "Saturdays 10:00 to 11:30am", or each weekly time. */
export function weeklyLongLabel(times: ClassWhen[]): string {
  const same = times.every(
    (t) =>
      t.start_local === times[0].start_local && t.duration === times[0].duration
  );
  if (same && times.length)
    return `${times.map((t) => DAYS_LONG[t.weekday]).join(" and ")} ${spanLabel(
      times[0].start_local,
      times[0].duration
    )}`;
  return times
    .map(
      (t) => `${DAYS_LONG[t.weekday]} ${spanLabel(t.start_local, t.duration)}`
    )
    .join("; ");
}

/**
 * The weekly times as seen in a time zone: staff pages use the browser's,
 * like the calendar. Monday first.
 */
export function inZone(times: ClassWhen[], zone: string): ClassWhen[] {
  return times
    .map((t) => {
      const local = moment.tz(t.next_start, zone);
      return {
        ...t,
        weekday: local.isoWeekday() - 1,
        start_local: local.format("HH:mm"),
      };
    })
    .sort(
      (a, b) =>
        a.weekday - b.weekday || a.start_local.localeCompare(b.start_local)
    );
}

/**
 * A class with one session, in one line in a time zone: "Sat, Oct 10 ·
 * 10:00am to 12:00pm". Undefined for a class with more sessions, which
 * reads by its weekly times and its dates.
 */
export function oneSessionLabel(
  klass: { weekly_times: ClassWhen[]; total_sessions: number },
  zone: string
): string | undefined {
  if (klass.total_sessions !== 1 || klass.weekly_times.length !== 1)
    return undefined;
  const [time] = klass.weekly_times;
  const start = moment.tz(time.next_start, zone);
  return `${start.format("ddd, MMM D")} · ${spanLabel(
    start.format("HH:mm"),
    time.duration
  )}`;
}

/** "Oct 10 to Dec 12". */
export function dateRange(first: string, last: string): string {
  return `${moment(first).format("MMM D")} to ${moment(last).format("MMM D")}`;
}

export const plural = (n: number, word: string) =>
  `${n} ${word}${n === 1 ? "" : "s"}`;

/** "7 of 12 seats", or "7 students" when there is no seat limit. */
export const seatsLabel = (enrolled: number, capacity?: number | null) =>
  capacity ? `${enrolled} of ${capacity} seats` : plural(enrolled, "student");
